import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { themeConfig } from "../config/themeConfig";
import * as Constants from "../utils/constants";

/**
 * Контекст темы приложения
 * @type {React.Context}
 * @property {Object} value - Значения контекста
 * @property {Object} value.theme - Текущая тема MUI
 * @property {Function} value.toggleTheme - Функция переключения темы
 * @property {string} value.mode - Текущий режим темы ('light' или 'dark')
 */
export const ThemeContext = createContext();

/**
 * Провайдер темы приложения
 * @component
 * @param {Object} props - Свойства компонента
 * @param {React.ReactNode} props.children - Дочерние компоненты
 * 
 * @property {string} mode - Текущий режим темы ('light'|'dark')
 * @property {Function} toggleTheme - Функция переключения темы
 * @property {Object} theme - Созданная тема MUI
 * 
 */
export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(() => {
        let savedMode = localStorage.getItem(Constants.THEME_MODE_KEY);
        if (savedMode === Constants.THEME_LIGHT || savedMode === Constants.THEME_DARK) {
            return savedMode;
        }

        let prefersDark = window.matchMedia(Constants.THEME_MEDIA_QUERY_PREFER_DARK).matches;
        return prefersDark ? Constants.THEME_DARK : Constants.THEME_LIGHT;
    }); 

    /**
     * Переключает тему приложения и сохраняет выбор в localStorage
     * @function
     * @param {string} newMode - Новый режим темы ('light' или 'dark')
     */
    const toggleTheme = (newMode) => {
        setMode(newMode);
        localStorage.setItem(Constants.THEME_MODE_KEY, newMode);
    };

    /**
     * Сброс на системную тему
     */
    const resetToSystemTheme = () => {
        localStorage.removeItem(Constants.THEME_MODE_KEY);
        const prefersDark = window.matchMedia(Constants.THEME_MEDIA_QUERY_PREFER_DARK).matches;
        setMode(prefersDark ? Constants.THEME_DARK : Constants.THEME_LIGHT);
    }

    // Слушаем изменения системной темы
    useEffect(() => {
        const mediaQuery = window.matchMedia(Constants.THEME_MEDIA_QUERY_PREFER_DARK);

        const handleChange = () => {
            if (!localStorage.getItem(Constants.THEME_MODE_KEY)) {
                setMode(mediaQuery.matches ? Constants.THEME_DARK : Constants.THEME_LIGHT)
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [])

    // Мемоизированная тема MUI
    const theme = useMemo(
        () => createTheme(themeConfig[mode]), 
        [mode]
    );

    return (
        <ThemeContext.Provider value={{theme, toggleTheme, mode, resetToSystemTheme}}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    )
}

/**
 * Хук для доступа к контексту темы
 * @function
 * @returns {Object} Контекст темы
 * @property {Object} theme - Текущая тема MUI
 * @property {Function} toggleTheme - Функция переключения темы
 * @property {string} mode - Текущий режим темы ('light' или 'dark')
 * 
 */
export const useThemeContext = () => useContext(ThemeContext);